import { AlertTriangle, Copy, KeyRound, RefreshCw, ShieldCheck, Smartphone, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { api } from "./api";
import "./two-factor.css";

interface TwoFactorStatus {
  enabled: boolean;
  remainingRecoveryCodes: number;
}

interface SetupResult {
  secret: string;
  otpauthUri: string;
}

interface ConfirmResult {
  enabled: true;
  recoveryCodes: string[];
  sessionsRevoked: true;
}

type DialogStage = "closed" | "manage" | "setup" | "recovery";

function twoFactorSettingRow(): HTMLElement | null {
  for (const row of document.querySelectorAll<HTMLElement>(".settings-grid .setting-row")) {
    if (row.querySelector("strong")?.textContent?.trim() === "Two-factor authentication") return row;
  }
  return null;
}

function applyToggleState(enabled: boolean): void {
  const toggle = twoFactorSettingRow()?.querySelector<HTMLButtonElement>(".toggle");
  if (!toggle) return;
  toggle.classList.toggle("on", enabled);
  toggle.setAttribute("aria-checked", String(enabled));
  toggle.title = enabled ? "Two-factor authentication is enabled" : "Set up two-factor authentication";
}

function authForm(): HTMLFormElement | null {
  const form = document.querySelector<HTMLFormElement>(".auth-form-wrap form");
  if (!form || form.querySelector('input[autocomplete="off"]')) return null;
  return form;
}

function showLoginError(form: HTMLFormElement, message: string): void {
  let error = form.querySelector<HTMLElement>(".security-login-error");
  if (!error) {
    error = document.createElement("div");
    error.className = "form-error security-login-error";
    const submit = form.querySelector('button[type="submit"]');
    if (submit) form.insertBefore(error, submit);
    else form.append(error);
  }
  error.textContent = message;
}

function ensureSecondFactorInput(form: HTMLFormElement): HTMLInputElement {
  const existing = form.querySelector<HTMLInputElement>("input[data-sleepy-second-factor]");
  if (existing) return existing;
  const label = document.createElement("label");
  label.className = "security-second-factor-label";
  label.textContent = "Authenticator or recovery code";
  const input = document.createElement("input");
  input.type = "text";
  input.autocomplete = "one-time-code";
  input.maxLength = 32;
  input.required = true;
  input.placeholder = "123456 or SLPY-XXXX-XXXX";
  input.setAttribute("data-sleepy-second-factor", "true");
  label.append(input);
  const error = form.querySelector(".form-error");
  if (error) form.insertBefore(label, error);
  else {
    const submit = form.querySelector('button[type="submit"]');
    if (submit) form.insertBefore(label, submit);
    else form.append(label);
  }
  window.setTimeout(() => input.focus(), 0);
  return input;
}

export function TwoFactorController() {
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [stage, setStage] = useState<DialogStage>("closed");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [setup, setSetup] = useState<SetupResult | null>(null);
  const [authenticatorCode, setAuthenticatorCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [disablePassword, setDisablePassword] = useState("");
  const [disableFactor, setDisableFactor] = useState("");
  const statusRequested = useRef(false);

  const refreshStatus = useCallback(async (force = false) => {
    if (statusRequested.current && !force) return;
    statusRequested.current = true;
    try {
      const next = await api<TwoFactorStatus>("/api/auth/totp/status");
      setStatus(next);
      applyToggleState(next.enabled);
    } catch {
      statusRequested.current = false;
    }
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (twoFactorSettingRow()) {
        if (status) applyToggleState(status.enabled);
        else void refreshStatus();
      }
      const form = authForm();
      const errorText = form?.querySelector(".form-error")?.textContent ?? "";
      if (form && errorText.includes("Two-factor authentication code is required")) ensureSecondFactorInput(form);
    });
    observer.observe(document.getElementById("root") ?? document.body, { childList: true, subtree: true, characterData: true });
    if (twoFactorSettingRow()) void refreshStatus();
    return () => observer.disconnect();
  }, [refreshStatus, status]);

  useEffect(() => {
    function openManager(event: MouseEvent) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const toggle = target.closest(".toggle");
      const row = toggle?.closest<HTMLElement>(".setting-row");
      if (!toggle || !row || row.querySelector("strong")?.textContent?.trim() !== "Two-factor authentication") return;
      event.preventDefault();
      event.stopPropagation();
      setError("");
      setNotice("");
      setSetup(null);
      setRecoveryCodes([]);
      setAuthenticatorCode("");
      setCurrentPassword("");
      setDisablePassword("");
      setDisableFactor("");
      setStage("manage");
      void refreshStatus(true);
    }
    document.addEventListener("click", openManager, true);
    return () => document.removeEventListener("click", openManager, true);
  }, [refreshStatus]);

  useEffect(() => {
    async function submitSecondFactorLogin(event: SubmitEvent) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form !== authForm()) return;
      const secondFactor = form.querySelector<HTMLInputElement>("input[data-sleepy-second-factor]");
      if (!secondFactor) return;
      event.preventDefault();
      event.stopImmediatePropagation();

      const username = form.querySelector<HTMLInputElement>('input[autocomplete="username"]')?.value ?? "";
      const password = form.querySelector<HTMLInputElement>('input[autocomplete="current-password"]')?.value ?? "";
      const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
      if (submit) submit.disabled = true;
      showLoginError(form, "Checking password and second factor…");
      try {
        await api<{ username: string }>("/api/auth/login", {
          method: "POST",
          body: JSON.stringify({ username, password, secondFactor: secondFactor.value.trim() }),
        });
        window.location.reload();
      } catch (caught) {
        showLoginError(form, caught instanceof Error ? caught.message : "Sign-in failed.");
        secondFactor.select();
      } finally {
        if (submit) submit.disabled = false;
      }
    }
    document.addEventListener("submit", submitSecondFactorLogin, true);
    return () => document.removeEventListener("submit", submitSecondFactorLogin, true);
  }, []);

  useEffect(() => {
    if (stage === "closed") return;
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape" && !busy && stage !== "recovery") setStage("closed");
    }
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [busy, stage]);

  async function beginSetup(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await api<SetupResult>("/api/auth/totp/setup", {
        method: "POST",
        body: JSON.stringify({ currentPassword }),
      });
      setSetup(result);
      setAuthenticatorCode("");
      setStage("setup");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not start two-factor setup.");
    } finally {
      setBusy(false);
    }
  }

  async function confirmSetup(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await api<ConfirmResult>("/api/auth/totp/confirm", {
        method: "POST",
        body: JSON.stringify({ code: authenticatorCode }),
      });
      setStatus({ enabled: true, remainingRecoveryCodes: result.recoveryCodes.length });
      applyToggleState(true);
      setRecoveryCodes(result.recoveryCodes);
      setCurrentPassword("");
      setSetup(null);
      setStage("recovery");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not verify the authenticator code.");
    } finally {
      setBusy(false);
    }
  }

  async function disableTwoFactor(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await api<{ enabled: false; sessionsRevoked: true }>("/api/auth/totp/disable", {
        method: "POST",
        body: JSON.stringify({ currentPassword: disablePassword, secondFactor: disableFactor }),
      });
      setStatus({ enabled: false, remainingRecoveryCodes: 0 });
      applyToggleState(false);
      setDisablePassword("");
      setDisableFactor("");
      setNotice("Two-factor authentication is disabled. Older sessions were revoked.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not disable two-factor authentication.");
    } finally {
      setBusy(false);
    }
  }

  async function copy(value: string, success: string) {
    try {
      await navigator.clipboard.writeText(value);
      setNotice(success);
      setError("");
    } catch {
      setError("The browser blocked clipboard access.");
    }
  }

  if (stage === "closed") return null;

  return <div className="two-factor-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy && stage !== "recovery") setStage("closed"); }}>
    <section className="two-factor-dialog" role="dialog" aria-modal="true" aria-labelledby="two-factor-title">
      <div className="two-factor-heading">
        <span><Smartphone /></span>
        <div><p className="eyebrow">Owner security</p><h2 id="two-factor-title">Two-factor authentication</h2><p>Add a six-digit authenticator code to every owner sign-in.</p></div>
        <button className="icon-button" type="button" aria-label="Close two-factor dialog" disabled={busy || stage === "recovery"} onClick={() => setStage("closed")}><X size={18} /></button>
      </div>

      {error && <div className="inline-error"><AlertTriangle size={16} /> {error}</div>}
      {notice && <div className="inline-success"><ShieldCheck size={16} /> {notice}</div>}

      {stage === "manage" && status?.enabled !== true && <form className="two-factor-form" onSubmit={(event) => void beginSetup(event)}>
        <div className="two-factor-state"><ShieldCheck size={20} /><div><strong>Currently off</strong><p>Setup verifies your password first. Nothing is enabled until your authenticator produces a valid code.</p></div></div>
        <label>Current password<input type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} autoComplete="current-password" maxLength={128} required autoFocus /></label>
        <div className="two-factor-actions"><button className="secondary-button" type="button" disabled={busy} onClick={() => setStage("closed")}>Cancel</button><button className="primary-button" type="submit" disabled={busy}>{busy ? <RefreshCw className="spin" size={16} /> : <Smartphone size={16} />} {busy ? "Starting…" : "Set up authenticator"}</button></div>
      </form>}

      {stage === "manage" && status?.enabled === true && <form className="two-factor-form" onSubmit={(event) => void disableTwoFactor(event)}>
        <div className="two-factor-state enabled"><ShieldCheck size={20} /><div><strong>Two-factor authentication is on</strong><p>{status.remainingRecoveryCodes} one-use recovery code{status.remainingRecoveryCodes === 1 ? "" : "s"} remaining.</p></div></div>
        <p className="two-factor-warning">Disabling 2FA requires both your current password and either a live six-digit code or one recovery code.</p>
        <label>Current password<input type="password" value={disablePassword} onChange={(event) => setDisablePassword(event.target.value)} autoComplete="current-password" maxLength={128} required /></label>
        <label>Authenticator or recovery code<input value={disableFactor} onChange={(event) => setDisableFactor(event.target.value)} autoComplete="one-time-code" maxLength={32} placeholder="123456 or SLPY-XXXX-XXXX" required /></label>
        <div className="two-factor-actions"><button className="secondary-button" type="button" disabled={busy} onClick={() => setStage("closed")}>Keep enabled</button><button className="secondary-button danger-button" type="submit" disabled={busy}>{busy ? <RefreshCw className="spin" size={16} /> : <KeyRound size={16} />} {busy ? "Disabling…" : "Disable 2FA"}</button></div>
      </form>}

      {stage === "manage" && status === null && <div className="two-factor-loading"><RefreshCw className="spin" size={20} /> Loading security status…</div>}

      {stage === "setup" && setup && <form className="two-factor-form" onSubmit={(event) => void confirmSetup(event)}>
        <div className="two-factor-step"><b>1</b><div><strong>Add TheSleepyBot to your authenticator app</strong><p>Choose “enter setup key” or “manual key,” then use this secret.</p></div></div>
        <div className="two-factor-secret"><code>{setup.secret}</code><button className="secondary-button" type="button" onClick={() => void copy(setup.secret, "Authenticator secret copied.")}><Copy size={14} /> Copy</button></div>
        <details className="two-factor-uri"><summary>Show advanced otpauth URI</summary><code>{setup.otpauthUri}</code><button className="text-button" type="button" onClick={() => void copy(setup.otpauthUri, "Authenticator URI copied.")}><Copy size={13} /> Copy URI</button></details>
        <div className="two-factor-step"><b>2</b><div><strong>Verify the setup</strong><p>Enter the current six-digit code from your authenticator. Codes rotate every 30 seconds.</p></div></div>
        <label>Six-digit code<input inputMode="numeric" autoComplete="one-time-code" value={authenticatorCode} onChange={(event) => setAuthenticatorCode(event.target.value.replace(/\D/g, "").slice(0, 6))} pattern="\d{6}" maxLength={6} placeholder="123456" required autoFocus /></label>
        <div className="two-factor-actions"><button className="secondary-button" type="button" disabled={busy} onClick={() => { setSetup(null); setStage("manage"); }}>Cancel setup</button><button className="primary-button" type="submit" disabled={busy || authenticatorCode.length !== 6}>{busy ? <RefreshCw className="spin" size={16} /> : <ShieldCheck size={16} />} {busy ? "Verifying…" : "Verify and enable"}</button></div>
      </form>}

      {stage === "recovery" && <div className="two-factor-recovery">
        <div className="two-factor-state enabled"><ShieldCheck size={20} /><div><strong>Two-factor authentication is enabled</strong><p>Save these recovery codes now. Each code works once if you lose your authenticator.</p></div></div>
        <div className="recovery-code-grid">{recoveryCodes.map((code) => <code key={code}>{code}</code>)}</div>
        <div className="two-factor-actions"><button className="secondary-button" type="button" onClick={() => void copy(recoveryCodes.join("\n"), "Recovery codes copied.")}><Copy size={14} /> Copy all codes</button><button className="primary-button" type="button" onClick={() => setStage("closed")}><ShieldCheck size={15} /> I saved these codes</button></div>
      </div>}
    </section>
  </div>;
}
